import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { QueriesStore, TabsStore, UserStore } from '../store/queriesStore';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { getQuery } from '../api/api';
import { toast } from 'react-toastify';

const STARTER_QUERIES_URL =
    'https://raw.githubusercontent.com/bitquery/streaming-data-platform-docs/refs/heads/main/docs/start/starter-queries.md';
const STARTER_SUBSCRIPTIONS_URL =
    'https://raw.githubusercontent.com/bitquery/streaming-data-platform-docs/refs/heads/main/docs/start/starter-subscriptions.md';

function parseMarkdownHeadings(md) {
    const tree = [];

    let currentH2 = null;
    let currentH3 = null;
    let currentItem = null;
    let readingDescription = false;

    const isIgnoredSection = title =>
        title === 'Table of Contents' || title === 'More APIs' || title === 'More on Streams';

    md.split('\n').forEach(rawLine => {
        const line = rawLine.trimEnd();

        if (line.startsWith('## ')) {
            const sectionTitle = line.slice(3).trim();
            if (!isIgnoredSection(sectionTitle)) {
                currentH2 = { title: sectionTitle, children: [] };
                tree.push(currentH2);
            } else {
                currentH2 = null;
            }
            currentH3 = null;
            currentItem = null;
            readingDescription = false;
            return;
        }

        if (!currentH2) return;

        if (line.startsWith('### ')) {
            const subsectionTitle = line.slice(4).trim();
            currentH3 = { title: subsectionTitle, children: [] };
            currentH2.children.push(currentH3);
            currentItem = null;
            readingDescription = false;
            return;
        }

        if (line.startsWith('#### ')) {
            const itemTitle = line.slice(5).trim();
            const parent = currentH3 || currentH2;
            if (!parent) return;

            currentItem = { title: itemTitle, description: '', url: '' };
            if (!parent.children) parent.children = [];
            parent.children.push(currentItem);
            readingDescription = true;
            return;
        }

        if (readingDescription && currentItem) {
            const text = line.trim();
            if (!text) return;

            if (/^[▶►]/.test(text)) {
                const match = text.match(/\[(.+?)\]\((.+?)\)/);
                if (match) currentItem.url = match[2];
                readingDescription = false;
                currentItem = null;
            } else {
                currentItem.description += (currentItem.description ? ' ' : '') + text;
            }
        }
    });

    return tree;
}

function useMarkdownTree(url) {
    const [tree, setTree] = useState([]);
    useEffect(() => {
        let isMounted = true;
        fetch(url)
            .then(res => (res.ok ? res.text() : Promise.reject(res.status)))
            .then(text => isMounted && setTree(parseMarkdownHeadings(text)))
            .catch(() => isMounted && setTree([]));
        return () => {
            isMounted = false;
        };
    }, [url]);
    return tree;
}

const StarterQueriesPanel = observer(({ type }) => {
    const history = useHistory();
    const { updateQuery, query, setQuery } = QueriesStore;
    const { index } = TabsStore;
    const { user } = UserStore;
    const url = type === 'queries' ? STARTER_QUERIES_URL : STARTER_SUBSCRIPTIONS_URL;
    const tree = useMarkdownTree(url);
    const [expandedKeys, setExpandedKeys] = useState(() => new Set());

    const toggleNode = useCallback((key) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const extractId = url => url ? url?.split('/')?.pop() : null;

    const handleItemClick = useCallback(
        async (url, title) => {
            const id = extractId(url);
            if (!id) return;
            try {
                const { data } = await getQuery(id);
                const queryData = {
                    ...data,
                    isOwner: data.account_id === user?.id,
                    saved: true
                }
                const isCurrentQueryModified = query[index]?.saved === false;
                if (isCurrentQueryModified) {
                    setQuery(queryData, data.id);
                } else {
                    updateQuery(queryData, index, data.id);
                }
                history.push(`/${id}`);
            } catch (e) {
                console.error(e);
                toast.error('Error opening query');
            }
        },
        [history, updateQuery, index, user, query, setQuery]
    );

    const renderTree = (nodes, parentKey = '') => (
        <ul className={parentKey ? 'tree-children' : 'tree-root'}>
            {nodes.map((node, idx) => {
                const key = parentKey ? `${parentKey}-${idx}` : `${type}-${idx}`;
                const hasChildren = Array.isArray(node.children) && node.children.length > 0;

                if (hasChildren) {
                    const isOpen = expandedKeys.has(key);
                    return (
                        <li key={key} className="tree-section">
                            <div
                                className={`tree-node ${isOpen ? 'node-open' : ''}`}
                                onClick={() => toggleNode(key)}
                            >
                                <span className="tree-toggle">{isOpen ? '▾' : '▸'}</span>
                                {node.title}
                            </div>
                            {isOpen && renderTree(node.children, key)}
                        </li>
                    );
                }

                const tipId = `tooltip-${key}`;
                return (
                    <li
                        key={key}
                        className="tree-child"
                        onClick={() => handleItemClick(node.url, node.title)}
                    >
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 100 }}
                            overlay={<Tooltip id={tipId}>{node.description}</Tooltip>}
                        >
                            <span className="child-link">
                                <i className="bi bi-terminal me-2"></i>
                                {node.title}
                            </span>
                        </OverlayTrigger>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <section className='starter-panel-container'>
            <div className="query-container ">
                {renderTree(tree)}
            </div>
        </section>
    );
});

export default StarterQueriesPanel; 